import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Refresh, Receipt, Cpu, Activity, Clock, Zap } from "@/components/icons";
import { Button } from "@/components/ui/button";

import { fetchBillingTotals, type BillingTotals } from "@/lib/api";
import { activeEstimateFigure, finalizedFigure, formatNumber, freshnessNote, totalsState, UNKNOWN } from "@/lib/billingTotals";
import { BillingFigureDetail } from "@/components/BillingFigureDetail";
import { Stat } from "@/components/Stat";
import { formatEnergy } from "@voltie/localization-web";
import { useT } from "@/app/localization";

export function BillingPage() {
  const t = useT();
  const q = useQuery({
    queryKey: ["billing", "system_totals"],
    queryFn: () => fetchBillingTotals(),
    refetchInterval: 30_000,
  });

  const d = q.data;
  // A failed refresh keeps the last good totals visible (with a note); only a first load
  // that fails shows the error. Never-published totals are still being calculated.
  const state = d ? totalsState(d) : null;
  const cold = state === "calculating";

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.unit.billing_title")}</h1>
          <div className="text-sm opacity-70">{t("operator.pricing.billing_subtitle")}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <Refresh className={`h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} /> {t("common.action.refresh")}
        </Button>
      </div>

      {q.isLoading || cold ? (
        <div className="flex flex-col items-center gap-3 p-12" role="status">
          <span className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
          {cold && <span className="text-sm opacity-70">{t("operator.pricing.billing_calculating")}</span>}
        </div>
      ) : !d || state === "unavailable" ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {d ? t("operator.pricing.billing_unavailable") : t("operator.pricing.billing_load_failed")}
        </div>
      ) : (
        <BillingTotalsGrid totals={d} refreshFailed={q.isError} />
      )}
    </div>
  );
}

function BillingTotalsGrid({ totals: d, refreshFailed }: { totals: BillingTotals; refreshFailed: boolean }) {
  const t = useT();
  const billed = finalizedFigure(t, d);
  const active = activeEstimateFigure(t, d);
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <Stat
          title={t("operator.pricing.billing_finalized")}
          value={<FigureValue value={billed.value} detail={<BillingFigureDetail figure={billed} />} />}
          description={t("operator.pricing.billing_finalized_hint")}
          icon={<Receipt className="h-4 w-4 opacity-60" />}
        />
        <Stat
          title={t("operator.pricing.billing_active_estimate")}
          value={<FigureValue value={active.value} detail={<BillingFigureDetail figure={active} />} />}
          description={t("operator.pricing.billing_active_estimate_hint")}
          icon={<Clock className="h-4 w-4 opacity-60" />}
        />
        <Stat
          title={t("operator.pricing.billing_total_chargers")}
          value={formatNumber(d.total_chargers)}
          description={t("operator.pricing.billing_total_chargers_completed_hint")}
          icon={<Cpu className="h-4 w-4 opacity-60" />}
        />
        <Stat
          title={t("operator.unit.total_sessions")}
          value={formatNumber(d.total_sessions)}
          description={t("operator.pricing.billing_total_sessions_hint")}
          icon={<Activity className="h-4 w-4 opacity-60" />}
        />
        <Stat
          title={t("operator.pricing.billing_total_energy")}
          value={d.total_energy_kwh == null ? UNKNOWN : formatEnergy(d.total_energy_kwh, "kWh", "summary")}
          description={t("operator.pricing.billing_total_energy_hint")}
          icon={<Zap className="h-4 w-4 opacity-60" />}
        />
      </div>

      <div className="text-xs opacity-50 text-center" data-testid="billing-freshness">
        {freshnessNote(t, d, refreshFailed) ??
          (d.cache_updated_at && t("operator.pricing.billing_updated_at", { time: new Date(d.cache_updated_at).toLocaleString() }))}
      </div>
    </>
  );
}

function FigureValue({ value, detail }: { value: string; detail: React.ReactNode }) {
  return (
    <>
      <div>{value}</div>
      {detail}
    </>
  );
}
